import type { ProjectData } from '@/shared/types/projectData';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface SyncProjectCacheParams {
  updatedData: ProjectData;
  operation: Record<string, any> | null;
  message: Record<string, any> | null;
  updateProjectInCache?: (projectId: string, updatedData: Partial<ProjectData>) => void;
  refreshProject?: (projectId: string) => Promise<void>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function syncProjectCacheFromUpdatedData({
  updatedData,
  operation,
  message,
  updateProjectInCache,
  refreshProject,
}: SyncProjectCacheParams) {
  const createdNodeId =
    (typeof message?.result?.node?.id === 'string' && message.result.node.id) ||
    (typeof operation?.data?.id === 'string' && operation.data.id) ||
    (typeof operation?.nodeId === 'string' && operation.nodeId) ||
    null;
  const createdNodeRawId = createdNodeId ? getNodeId(String(createdNodeId)) : null;

  const targetProjectId =
    message?.projectId ||
    message?.project_id ||
    operation?.metadata?.projectId ||
    operation?.projectId ||
    (typeof operation?.nodeId === 'string' ? getProjectIdFromNodeId(operation.nodeId, updatedData.projectId) : null) ||
    updatedData.projectId;

  if (!targetProjectId || !updateProjectInCache) {
    return;
  }

  const isColumnOperation =
    operation?.type === 'add-column' ||
    operation?.type === 'update-column' ||
    operation?.type === 'delete-column' ||
    operation?.type === 'reorder-columns';

  const scopedNodes = updatedData.nodes
    .filter((node) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodeRecord = node as any;
      if (nodeRecord._deleted || nodeRecord.isDeleted) {
        return false;
      }

      const nodeProjectId =
        nodeRecord.projectId ||
        (operation?.type === 'create' &&
        createdNodeRawId &&
        getNodeId(String(node.id)) === createdNodeRawId
          ? targetProjectId
          : null) ||
        (typeof node.id === 'string' ? getProjectIdFromNodeId(String(node.id), updatedData.projectId) : null) ||
        updatedData.projectId;

      return nodeProjectId === targetProjectId;
    })
    .map((node) => ({
      ...node,
      id: getNodeId(String(node.id)),
      parentId: node.parentId ? getNodeId(String(node.parentId)) : node.parentId,
      dependencies: Array.isArray(node.dependencies)
        ? node.dependencies.map((dependencyId: string) => getNodeId(String(dependencyId)))
        : node.dependencies,
    }));

  const rootNodeIds = scopedNodes
    .filter((node) => !node.parentId)
    .sort((left, right) => {
      const leftOrder = typeof left.order === 'number' ? left.order : 0;
      const rightOrder = typeof right.order === 'number' ? right.order : 0;
      return leftOrder - rightOrder;
    })
    .map((node) => node.id);

  const cachePayload: Partial<ProjectData> = {
    nodes: scopedNodes,
    rootNodeIds,
  };

  const dataIsProjectScoped =
    updatedData.projectId === targetProjectId ||
    !updatedData.nodes.some((node) => typeof node.id === 'string' && node.id.includes(':'));

  if (isColumnOperation && dataIsProjectScoped) {
    cachePayload.kanbanColumns = updatedData.kanbanColumns;
  }

  updateProjectInCache(targetProjectId, cachePayload);

  if (isColumnOperation && !dataIsProjectScoped && refreshProject) {
    void refreshProject(targetProjectId).catch(() => {});
  }
}
